import React, { useState, useCallback } from 'react';
import { Card, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Select from 'react-select';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faBuilding,
  faFileAlt,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faStickyNote as faNote,
} from '@fortawesome/free-solid-svg-icons';

const BankStatementImporter = () => {
  const { companyId } = useParams();

  const banks = [
    { value: 'Swedbank', label: 'Swedbank' },
    { value: 'SEB', label: 'SEB' },
    { value: 'Handelsbanken', label: 'Handelsbanken' },
    { value: 'Nordea', label: 'Nordea' },
    { value: 'Danske Bank', label: 'Danske Bank' },
    { value: 'Länsförsäkringar Bank', label: 'Länsförsäkringar Bank' },
  ].map(bank => ({
    ...bank,
    icon: <FontAwesomeIcon icon={faNote} className="me-2" />
  }));

  const [selectedBank, setSelectedBank] = useState(null);
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setFileError(null);
    setImportResult(null);

    if (rejectedFiles && rejectedFiles.length > 0) {
      setFileError('Only CSV files are allowed. Please select a file ending with .csv');
      setFile(null);
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      // Additional validation: check file extension
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setFileError('File must be a CSV file (.csv)');
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedBank) {
      alert('Please select a bank from the dropdown.');
      return;
    }
    if (!file) {
      alert('Please select a CSV file to import.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/banking/import?bankName=${encodeURIComponent(
          selectedBank.value
        )}`
      );
      const response = await axios.post(apiUrl, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
    } catch (error) {
      console.error('Error importing bank statement:', error);
      setImportResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      border: '2px solid #e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        border: '2px solid #cbd5e0',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#ebf8ff' : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
    }),
  };

  return (
    <div className="container py-4">
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Header className="py-3">
          <h4 className="mb-0 text-primary">
            <FontAwesomeIcon icon={faUpload} className="me-2" />
            Import Bank Statement
          </h4>
        </Card.Header>
        <Card.Body className="p-4">
          <div className="mb-4">
            <label htmlFor="bankSelect" className="form-label fw-bold">
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              Select Bank
            </label>
            <Select
              id="bankSelect"
              options={banks}
              value={selectedBank}
              onChange={setSelectedBank}
              placeholder="Select your bank..."
              styles={customSelectStyles}
              formatOptionLabel={bank => (
                <div className="d-flex align-items-center">
                  {bank.icon}
                  {bank.label}
                </div>
              )}
            />
          </div>

          {/* File Error Alert */}
          {fileError && (
            <div className="alert alert-warning mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {fileError}
            </div>
          )}

          <div
            {...getRootProps()}
            className={`border border-2 border-dashed rounded p-5 text-center ${
              fileError ? 'border-danger' : 'border-primary'
            }`}
            style={{ cursor: 'pointer' }}
          >
            <input {...getInputProps()} accept=".csv" />
            <FontAwesomeIcon
              icon={faFileAlt}
              className={`display-4 mb-3 ${fileError ? 'text-danger' : 'text-primary'}`}
            />
            {isDragActive ? (
              <p className="h5 mb-0 text-primary">Drop the CSV file here...</p>
            ) : (
              <div>
                <p className="h5 mb-2">
                  Drag and drop a CSV file here, or click to select
                </p>
                {file && (
                  <p className="mb-0 text-success">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Selected: {file.name}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 text-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleUpload}
              disabled={loading}
              className="px-5 py-2"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Importing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Import Bank Statement
                </>
              )}
            </Button>
          </div>

          {importResult && (
            <div className="mt-4">
              {importResult.success ? (
                <div className="alert alert-success d-flex align-items-start">
                  <FontAwesomeIcon icon={faCheckCircle} className="mt-1 me-2" />
                  <div>
                    <p className="mb-2">
                      {importResult.message || `Successfully imported ${importResult.statistics?.successful || 0} transactions.`}
                    </p>
                    {importResult.statistics && (
                      <div className="mb-2">
                        <small className="text-muted">
                          Total: {importResult.statistics.total} |
                          Successful: {importResult.statistics.successful} |
                          Duplicates: {importResult.statistics.duplicates} |
                          Failed: {importResult.statistics.failed}
                        </small>
                      </div>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <>
                        <p className="mb-2">Some rows had issues:</p>
                        <ul className="mb-0">
                          {importResult.errors.slice(0, 10).map((err, index) => (
                            <li key={index}>
                              Row {err.row}: {err.message || err.error}
                            </li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-muted">... and {importResult.errors.length - 10} more</li>
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger d-flex align-items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <span>Error: {importResult.error || importResult.message}</span>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BankStatementImporter;